// draftDrive describes the campaign's flow from the editor's current values —
// saved or not — in the shape flowGraph takes (the shape the Test drive
// start returns), so the campaign page can draw the flow as it's built. It
// mirrors the server's reading of the same settings (campaigns_api.go:
// signupEmailConfirms, trackedLinks, liveMailVariants; subscriptions:
// signupEmailProblem). Pure, so it's unit-tested.
//
//   record          the saved campaign (forms, signup emails, tracked links)
//   flow            the settings form's current values: { doubleOptIn,
//                   includeIncentive, incentiveDelivery, confirmedUrl,
//                   thankYouUrl, senderEmailId }
//   incentive       the reward as saved (null: none set)
//   senders         the org's verified senders ([{ id, email }])

import { FOLLOWUP_DEFAULTS } from "../followupDefaults";
import { BUILTIN_CONFIRM_LABEL, designsFor, emailPool } from "../builtinEmail";

const CONFIRM_TAG = /\{\{\s*\.SystemVars\.Confirm(Url|AndClickLink)\b/;
const TRACKED_TAG = /\{\{\s*\.SystemVars\.ConfirmAndClickLink\s*\}\}\/([A-Za-z0-9_-]+)/g;

const noId = (id) => !id || id === "null" || id === "undefined";

export function draftDrive({ record, flow, incentive, senders }) {
  const r = record || {};
  const f = flow || {};
  // The signup emails sent: the slot's (built-in or its own designs).
  const pool = emailPool(r);
  const live = pool.filter((m) => m && m.should_run);
  const templates = (m) => `${m.mail_html_template || ""}\n${m.mail_text_template || ""}`;

  const tracked = [];
  live.forEach((m) => {
    for (const match of templates(m).matchAll(TRACKED_TAG)) {
      const name = match[1];
      if (tracked.some((t) => t.name === name)) continue;
      const link = (r.link_redirects || {})[name];
      tracked.push({ name, destination: (link && link.destination) || "" });
    }
  });

  let problem = null;
  if (noId(f.senderEmailId)) {
    problem = { text: "No sender is set, so the signup email can't be sent. Choose who it's sent from.", fix: "settings", fix_field: "sender", short: "No sender set" };
  } else if (!(senders || []).some((s) => s.id === f.senderEmailId)) {
    problem = { text: "The sender isn't a verified sender (it was removed, or isn't verified yet), so the signup email can't be sent. Choose a verified sender.", fix: "settings", fix_field: "sender", short: "Sender not verified" };
  } else if (!pool.length) {
    problem = r.signup_email_source === "custom"
      ? { text: "The signup email is set to your own design, but there isn't one yet, so nothing would be sent. Make one under Emails, or switch it to the built-in email.", fix: "signup_email", fix_field: "add_signup_email", short: "No design yet" }
      : { text: "There's no signup email yet, so nothing would be sent. Set one up under Emails.", fix: "signup_email", fix_field: "add_signup_email", short: "No signup email yet" };
  }

  const inc = incentive;
  return {
    double_opt_in: !!f.doubleOptIn,
    include_incentive: !!f.includeIncentive,
    incentive_delivery: f.incentiveDelivery || "redirect",
    confirm_email_url: (f.confirmedUrl || "").trim(),
    form_thank_you_url: (f.thankYouUrl || "").trim(),
    reward_limits: inc ? rewardLimits(inc) : null,
    forms: (r.form_variants || []).map((v) => ({ id: v.id, name: v.name, should_run: v.should_run })),
    live_signup_emails: live.length,
    // the built-in email has a Confirm button exactly when they're asked to confirm
    signup_email_confirms: live.some((m) => (m.builtin ? !!f.doubleOptIn : CONFIRM_TAG.test(templates(m)))),
    signup_email_source: r.signup_email_source === "custom" ? "custom" : "builtin",
    followup_email_source: r.followup_email_source === "custom" ? "custom" : "builtin",
    followup_designs: designsFor(r, "followup").length,
    tracked_links: tracked,
    email_problem: problem,
  };
}

// templateLabels reads the live signup emails' buttons' own words from their
// templates (for the graph, before any email is sent): the Confirm button,
// tracked links, and the reward button the reward adds ({ confirm, reward,
// links }, as buttonLabels gives for an arrived email).
export function templateLabels(record, incentive, doubleOptIn = false) {
  const out = {};
  const links = {};
  const text = (html) => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  emailPool(record).filter((m) => m && m.should_run).forEach((m) => {
    if (m.builtin) {
      if (doubleOptIn && !out.confirm) out.confirm = BUILTIN_CONFIRM_LABEL;
      return;
    }
    const anchors = /<a\b[^>]*href\s*=\s*["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi;
    let a;
    while ((a = anchors.exec(m.mail_html_template || ""))) {
      const href = a[1];
      const words = text(a[2]);
      if (!words) continue;
      const tracked = /\{\{\s*\.SystemVars\.ConfirmAndClickLink\s*\}\}\/([A-Za-z0-9_-]+)/.exec(href);
      if (tracked) {
        if (!links[tracked[1]]) links[tracked[1]] = words;
      } else if (!out.confirm && /\{\{\s*\.SystemVars\.ConfirmUrl\s*\}\}/.test(href)) {
        out.confirm = words;
      }
    }
  });
  if (Object.keys(links).length) out.links = links;
  // Mirrors incentive.ButtonLabel.
  if (incentive) out.reward = incentive.link_url ? (incentive.link_label || "").trim() || "Get your reward" : `Download ${incentive.file_name || "your file"}`;
  return out;
}

// rewardLimits is the reward as the server's RewardLimits describes it
// (TestRewardLimits): its limits, and the follow-up email that brings it.
function rewardLimits(inc) {
  const defaults = FOLLOWUP_DEFAULTS[inc.link_url ? "link" : "file"];
  const subject = (inc.followup_subject || "").trim();
  const body = (inc.followup_body || "").trim();
  return {
    uses: inc.default_uses || 5, expiry_hours: inc.expiry_hours || 48, unlimited: !!inc.unlimited, link: !!inc.link_url,
    followup_subject: subject ? inc.followup_subject : defaults.subject,
    followup_body: body ? inc.followup_body : defaults.body,
    followup_default: !subject && !body,
  };
}
